/**
 * PRODUÇÃO — a FORMA e o MOVIMENTO da casca do `Composer` entre a pílula de uma linha e a
 * casca expandida, e a MEDIDA do campo que decide entre as duas (`useAutoGrow`) — ver o
 * cabeçalho de `composer.tsx`. Fica aqui, e não em `composer.tsx`, para a casca caber no
 * teto de linhas dela.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/* O ponteiro é um dedo (`pointer: coarse`): no toque o Enter do teclado virtual quebra
   linha e o ditado não devolve o foco ao campo (ver o cabeçalho de `composer.tsx`). Quem
   pergunta lê UMA vez, no mount — o dedo não vira mouse no meio do uso. */
export const isCoarsePointer = (): boolean => globalThis.matchMedia('(pointer: coarse)').matches;

/* A FORMA da casca: com `header`, com o aviso do ditado ou EXPANDIDA a stadium shape vira
   `rounded-3xl` (a curva de 100px engole a primeira e a última linha de qualquer casca
   mais alta que a pílula). */
export function shellShape(header: boolean, status: boolean, expanded: boolean): string {
  // `overflow-hidden` só com `header`: recorta a faixa no raio; na pílula sem faixa
  // ele não tem o que recortar e limitaria um popover futuro dos controles.
  if (header) return 'overflow-hidden rounded-3xl';
  return status || expanded ? 'rounded-3xl' : 'rounded-full';
}

/* A expansão é uma troca DISCRETA de layout (`flex-wrap` + `order` + `basis-full` lidos
   do `data-expanded`): a altura vai de `auto` a `auto`, e `transition: height` não
   interpola `auto` (`interpolate-size`/`calc-size()` só existem no Chromium — o app roda
   em WKWebView). Sem isto a pílula pulava de 52px à altura das duas linhas num frame só,
   com o raio (que TEM transição) chegando atrasado.

   FLIP da altura com a Web Animations API: mede a altura ANTES da troca (guardada no
   último commit), a altura DEPOIS (o DOM já está no estado novo quando o layout effect
   roda) e anima uma na outra, voltando a `auto` ao terminar — o auto-grow do campo e as
   faixas (header, aviso do ditado) seguem mandando na altura fora da animação. `overflow:
   clip` só ENQUANTO anima: no meio do caminho a casca é mais baixa que o conteúdo e a
   linha de controles vazaria por baixo dela; fora da animação a casca não recorta (um
   popover dos controles precisa sair dela). Uma troca no meio de outra parte da altura
   VISUAL do momento, não da que a anterior perseguia — sem salto ao inverter o gesto.

   O RAIO vai junto nos mesmos keyframes: `rounded-full` é `calc(infinity * 1px)`, e a
   transição CSS dele para `rounded-3xl` passa quase toda a duração clampada em metade da
   altura e salta no fim (o meio do caminho mostrava a stadium shape recortada). Aqui os
   dois extremos são o raio EFETIVO (o declarado limitado a metade da altura), e a
   animação WAAPI vence a transição da classe enquanto roda.

   Duração e curva vêm dos tokens do DS (`--duration-3`, abre/fecha de camada, e
   `--ease-standard`), lidos do computado para não duplicar valor; `prefers-reduced-motion`
   pula a animação, como o placeholder e a `enter`. Sem `Element.animate` (jsdom) não faz
   nada — o layout já está certo, só não anima. */

const FALLBACK_DURATION_MS = 200;
const FALLBACK_EASING = 'ease-out';

function motionSettings(el: HTMLElement): { duration: number; easing: string } {
  const style = getComputedStyle(el);
  const duration = Number.parseFloat(style.getPropertyValue('--duration-3'));
  const easing = style.getPropertyValue('--ease-standard').trim();
  return {
    duration: Number.isFinite(duration) && duration > 0 ? duration : FALLBACK_DURATION_MS,
    easing: easing === '' ? FALLBACK_EASING : easing,
  };
}

/** O raio efetivo do canto: o declarado, limitado a metade da altura (é o que o browser
 *  desenha — `rounded-full` declara infinito). */
function cornerRadius(el: HTMLElement, height: number): number {
  const declared = Number.parseFloat(getComputedStyle(el).borderTopLeftRadius);
  return Math.min(Number.isFinite(declared) ? declared : Infinity, height / 2);
}

interface Shape {
  height: number;
  radius: number;
}

const shapeOf = (el: HTMLElement): Shape => {
  const height = el.getBoundingClientRect().height;
  return { height, radius: cornerRadius(el, height) };
};

const keyframeOf = (shape: Shape) => ({
  height: `${String(shape.height)}px`,
  borderRadius: `${String(shape.radius)}px`,
});

function motionAllowed(el: HTMLElement): boolean {
  return (
    typeof el.animate === 'function' &&
    !globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useExpandMotion(pillRef: RefObject<HTMLElement | null>, expanded: boolean): void {
  /* A última FORMA natural (altura e raio) vista num commit sem animação em curso — o
     "antes" da FLIP. */
  const settled = useRef<Shape | null>(null);
  const running = useRef<Animation | null>(null);
  /* O `expanded` do último commit: a FLIP só roda quando ele TROCA (no mount não há
     "antes" para animar). */
  const lastExpanded = useRef(expanded);

  useLayoutEffect(() => {
    const changed = lastExpanded.current !== expanded;
    lastExpanded.current = expanded;
    const el = pillRef.current;
    if (!el || !changed) return;
    // Trocou no meio de uma animação: parte de onde a casca está visualmente, e cancela
    // antes de medir para a altura natural do estado novo não vir contaminada.
    const from = running.current ? shapeOf(el) : settled.current;
    running.current?.cancel();
    running.current = null;
    const to = shapeOf(el);
    settled.current = to;
    if (from === null || from.height === to.height || !motionAllowed(el)) return;
    el.style.overflow = 'clip';
    const animation = el.animate([keyframeOf(from), keyframeOf(to)], {
      ...motionSettings(el),
      fill: 'none',
    });
    running.current = animation;
    const settle = () => {
      if (running.current !== animation) return;
      running.current = null;
      el.style.removeProperty('overflow');
    };
    animation.addEventListener('finish', settle);
    animation.addEventListener('cancel', settle);
  }, [pillRef, expanded]);

  // Todo commit fora de animação renova o "antes": o campo cresceu com o texto, uma faixa
  // entrou ou saiu — a próxima troca parte da altura real, não da de quando expandiu.
  useLayoutEffect(() => {
    const el = pillRef.current;
    if (!el || running.current) return;
    settled.current = shapeOf(el);
  });
}

/** Teto do auto-grow (px); acima disso o campo libera scroll interno. */
const MAX_FIELD_PX = 200;

/** A entrelinha do campo quando o computado não responde (jsdom): a do `text-body`. */
const FALLBACK_LINE_PX = 22;

/* A altura a partir da qual o campo tem MAIS de uma linha: o respiro vertical (`py-2`)
   mais uma linha e meia — o meio do caminho entre uma e duas, para arredondamento de
   fonte não decidir. Lido do computado para acompanhar `pointer-coarse:text-base`. */
function wrapThresholdPx(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const line = Number.parseFloat(style.lineHeight);
  const pad =
    (Number.parseFloat(style.paddingTop) || 0) + (Number.parseFloat(style.paddingBottom) || 0);
  return pad + 1.5 * (Number.isFinite(line) && line > 0 ? line : FALLBACK_LINE_PX);
}

/* O texto passa de uma linha quando o campo tem `width` de largura? `null` mede na largura
   que o campo já tem. A sonda é um `max-width` temporário: `flex-grow` o respeita, e a
   leitura síncrona de `scrollHeight` sai antes de qualquer pintura — o observer de
   largura não vê a ida e volta. */
function wrapsAt(el: HTMLElement, width: number | null): boolean {
  if (width !== null) el.style.maxWidth = `${String(width)}px`;
  const height = el.scrollHeight;
  if (width !== null) el.style.removeProperty('max-width');
  return height > wrapThresholdPx(el);
}

/* PRODUÇÃO — o auto-grow do campo: acompanha o texto até o teto e só então libera o
   scroll interno. Recalcula também quando a LARGURA muda (breakpoint, rotação, teclado
   virtual): o texto reflui para outra contagem de linhas e o onChange nunca dispara
   nesses casos. Guarda na largura para a nossa própria escrita de height não
   re-disparar o observer. (A largura muda também quando a casca expande: o campo passa
   de um terço da linha à linha inteira, e o texto reflui.)

   É daqui também que sai se o texto PASSA DE UMA LINHA (`onWrap`), o que decide a casca
   expandida. A medida é sempre contra a largura RECOLHIDA do campo: recolhida, é a
   largura que ele tem; expandida, é a largura da pílula menos a pegada dos controles na
   linha (`footprint`, guardada no último layout recolhido — os controles têm largura
   fixa, então a conta sobrevive a rotação e breakpoint). Medir a largura atual
   oscilaria: expandido, o campo é mais largo e o mesmo texto cabe numa linha. Fora de
   cena (`hidden`, durante o ditado) não mede nada: as larguras são zero. */
export function useAutoGrow(
  getField: () => HTMLElement | null,
  getPill: () => HTMLElement | null,
  onWrap: (wraps: boolean) => void,
) {
  const footprint = useRef<number | null>(null);
  const grow = useCallback(() => {
    const el = getField();
    const pill = getPill();
    if (!el || !pill || el.closest('[hidden]')) return;
    el.style.height = 'auto';
    const height = el.scrollHeight;
    const expandedNow = pill.dataset['expanded'] === 'true';
    if (!expandedNow) footprint.current = pill.clientWidth - el.clientWidth;
    const collapsedWidth =
      expandedNow && footprint.current !== null ? pill.clientWidth - footprint.current : null;
    onWrap(wrapsAt(el, collapsedWidth));
    el.style.height = `${String(Math.min(height, MAX_FIELD_PX))}px`;
    el.style.overflowY = height > MAX_FIELD_PX ? 'auto' : 'hidden';
  }, [getField, getPill, onWrap]);
  useEffect(() => {
    const el = getField();
    if (!el) return undefined;
    grow();
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return;
      lastWidth = el.clientWidth;
      grow();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [getField, grow]);
  return grow;
}
